import { isNil } from 'lodash';
import { useCallback } from 'react';
import { AiSummary } from '@shared/scenes/ask/components/AiSummary';
import { useAiSummariesEnabled } from '@shared/scenes/ask/useAiSummariesEnabled';
import { getComponentLabelWithFallback, isSubformComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { buildCardSummaryPrompt } from './buildCardSummaryPrompt';
import { useCardSummary } from './useCardSummary';

interface Props {
  /** The card (workflow-entity) id — the Ask scope the summary is resolved against. */
  entityId: string;
  /** The card's own workflow template id, so the prompt can distinguish it from its subforms. */
  workflowTemplateId: string;
  /** All components on the card; subform components are surfaced to the prompt by name and template. */
  components: API.AnyComponent[];
}

/**
 * "Summarize" widget for a whole card, shown at the top of the entity form. Scopes the
 * Ask agent to this card so it can consider all fields, subform contents, and related
 * cards. See {@link buildCardSummaryPrompt}. Renders nothing unless AI summaries are
 * enabled and a card-summary provider is present (i.e. the editable card view).
 */
export function CardSummary({ entityId, workflowTemplateId, components }: Props) {
  const isEnabled = useAiSummariesEnabled();
  const cardSummary = useCardSummary();

  const buildPrompt = useCallback(() => {
    const subforms = components.filter(isSubformComponent).map(component => ({
      name: getComponentLabelWithFallback(component),
      workflowTemplateIds: component.workflowTemplateIds ?? [],
    }));
    return buildCardSummaryPrompt({ workflowTemplateId, subforms });
  }, [workflowTemplateId, components]);

  if (!isEnabled || isNil(cardSummary)) {
    return null;
  }

  return (
    <AiSummary
      scope={{ type: 'card', id: entityId }}
      buildPrompt={buildPrompt}
      summary={cardSummary.summary}
      onSummaryGenerated={cardSummary.setSummary}
      testIDPrefix="card"
    />
  );
}
